import React, { useCallback, useRef, useState } from 'react'
import {
  AppInputFile,
  AppInputFileColor,
} from '../../../components/AppInput/AppInputFile'
import {
  AppImagePopup,
  IAppImagePopup,
} from '../../../components/AppImagePopup/AppImagePopup'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { useOutsideClick } from '../../../utils/useOutsideClick'
import { useTranslation } from 'react-i18next'
import imageCompression from 'browser-image-compression'
import './style.scss'
import heic2any from 'heic2any'
import { blobToFile } from 'utils/blobToFile'
import { convertWebPToJpeg } from 'utils/convertWebPToJpeg'
interface IDragAndDropUploadPhotoProps {
  fileURL: string | null
  onSetFile: (file: File | null) => void
  setFilePreview: (file: string) => void
  setFileFromCamera?: (value: boolean) => void
  isLoading?: boolean
  setIsDirty?: (value: boolean) => void
  setIsDeletedFile?: (value: boolean) => void
  mt?: boolean
  disabled?: boolean
}
const options = {
  maxSizeMB: 0.5, // Максимальный размер 0.5 MB
  maxWidthOrHeight: 800, // Уменьшение размера изображения
  useWebWorker: true,
}
export const DragAndDropUploadPhoto = ({
  fileURL,
  onSetFile,
  setFilePreview,
  mt,
  setFileFromCamera,
  isLoading,
  setIsDirty,
  setIsDeletedFile,
  disabled,
}: IDragAndDropUploadPhotoProps) => {
  const { t } = useTranslation()
  const [drag, setDrag] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingConversion, setLoadingConversion] = useState(false)
  const [active, setActive] = useState(false)
  const [imagePopup, setImagePopup] = useState<IAppImagePopup>({
    active: false,
    photo: null,
    title: null,
  })
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [showLoaderConversion, setShowLoaderConversion] =
    useState<boolean>(false)
  const handleLoading = (isLoading: boolean) => {
    if (isLoading) {
      setShowLoader(false)
      setTimeout(() => {
        setShowLoader(true)
      }, 700) // Показываем лоадер, только если прошло 0.7 секунды
    } else {
      setShowLoader(false)
    }
    setLoading(isLoading)
  }
  const handleLoadingConversion = (isLoading: boolean) => {
    if (isLoading) {
      setShowLoaderConversion(false)
      setTimeout(() => {
        setShowLoaderConversion(true)
      }, 700) // Показываем лоадер, только если прошло 0.7 секунды
    } else {
      setShowLoaderConversion(false)
    }
    setLoadingConversion(isLoading)
  }
  const removeFile = useCallback(() => {
    onSetFile(null)
    setFilePreview('')
    setActive(false)
    setIsDeletedFile?.(true)
    setIsDirty && setIsDirty(true)
    setLoadingConversion(false)
    setLoading(false)
  }, [])
  const refMenu = useRef<HTMLDivElement | null>(null)

  const dragStartHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDrag(true)
  }
  const dragLeaveHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDrag(false)
  }
  const onDropHandler = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const fileList = e.dataTransfer.files
    let file = fileList[0]
    if (!file) return
    if (file.name.endsWith('.heic')) {
      handleLoadingConversion(true)
      try {
        const imageBlob = (await heic2any({
          blob: file,
          toType: 'image/jpeg', // Конвертация в JPEG
        })) as Blob
        file = blobToFile(imageBlob, file.name.replace('.heic', '.jpg'))
      } catch (error) {
        setLoadingConversion && setLoadingConversion(false)
        console.error('Ошибка сжатия:', error)
      }
    }
    if (file.type === 'image/webp') {
      handleLoadingConversion(true)
      file = await convertWebPToJpeg(file)
    }
    handleLoading(true)
    try {
      const compressedFile = await imageCompression(file, options)
      onSetFile(compressedFile)
    } catch (error) {
      console.error('Ошибка сжатия:', error)
    }
    setLoading(false)
    setLoadingConversion(false)
    setDrag(false)
    setIsDirty && setIsDirty(true)
  }

  useOutsideClick(refMenu, () => setActive(false), active)

  const onSetFileHandler = useCallback(async (file: File | null) => {
    setActive(false)
    onSetFile(null)
    setFilePreview('')
    setIsDirty && setIsDirty(true)
    if (!file) return
    if (file.name.toLocaleLowerCase().endsWith('.heic')) {
      handleLoadingConversion(true)
      try {
        const imageBlob = (await heic2any({
          blob: file,
          toType: 'image/jpeg', // Конвертация в JPEG
        })) as Blob
        file = blobToFile(imageBlob, file.name.replace('.heic', '.jpg'))
      } catch (error) {
        setLoadingConversion && setLoadingConversion(false)
        console.error('Ошибка сжатия:', error)
      }
    }
    if (file.type === 'image/webp') {
      handleLoadingConversion(true)
      file = await convertWebPToJpeg(file)
    }
    handleLoading(true)
    try {
      const compressedFile = await imageCompression(file, options)
      onSetFile(compressedFile)
    } catch (error) {
      console.error('Ошибка сжатия:', error)
    }
    setLoading(false)
    setLoadingConversion(false)
    onSetFile(file)
  }, [])

  const closeImagePopupHandler = useCallback(() => {
    setImagePopup({ active: false, photo: null, title: null })
  }, [])
  const openImagePopupHandler = useCallback((photo: string, title: string) => {
    setImagePopup({ active: true, photo, title })
  }, [])

  if (!!fileURL) {
    return (
      <React.Fragment>
        {imagePopup.active && imagePopup.photo && (
          <AppImagePopup
            title={imagePopup.title || ''}
            img={imagePopup.photo}
            setActive={closeImagePopupHandler}
          />
        )}
        <div className={`upload-photo-block with-photo ${mt ? 'mt-20' : ''}`}>
          <div
            className="loupe-block"
            onClick={() => openImagePopupHandler(fileURL, '')}
          >
            <i className="an-ico an-ico-search" />
          </div>
          <div
            className="photo-menu-ico"
            onClick={() => setActive((prevState) => !prevState)}
          >
            <i className="an-ico an-ico-dotes-v" />
          </div>
          {active && !disabled && (
            <div className="photo-menu" ref={refMenu}>
              <div className="photo-menu-item">
                <i className="an-ico an-ico-reload" />
                <AppInputFile
                  onChangeFile={onSetFileHandler}
                  setFilePreview={setFilePreview}
                  setLoading={handleLoading}
                  setLoadingConversion={handleLoadingConversion}
                  text={t('Заменить фото')}
                  color={AppInputFileColor.gray}
                />
              </div>
              {setFileFromCamera && (
                <div
                  className="photo-menu-item"
                  onClick={() => setFileFromCamera(true)}
                >
                  <i className="an-ico an-ico-camera" />
                  {t('Сфотографировать станцией')}
                </div>
              )}
              {!!setIsDeletedFile && (
                <div className="photo-menu-item" onClick={removeFile}>
                  <i className="an-ico an-ico-basket" />
                  {t('Удалить')}
                </div>
              )}
            </div>
          )}
          <img src={fileURL} alt={'photo'} />
        </div>
      </React.Fragment>
    )
  }

  return (
    <>
      {drag && !disabled ? (
        <div
          className="upload-photo-block is-draggable"
          onDragStart={(e) => dragStartHandler(e)}
          onDragLeave={(e) => dragLeaveHandler(e)}
          onDragOver={(e) => dragStartHandler(e)}
          onDrop={(e) => onDropHandler(e)}
        >
          {loadingConversion && showLoaderConversion ? (
            <span className="loading-text">
              {t('Конвертируем изображение...')}
            </span>
          ) : showLoader && loading ? (
            <span className="loading-text">{t('Сжимаем изображение...')}</span>
          ) : (
            <span>{t('Отпустите файл, чтобы загрузить его')}</span>
          )}
        </div>
      ) : (
        <div
          className={`upload-photo-block flex-col ${mt ? 'mt-20' : ''}`}
          onDragStart={(e) => dragStartHandler(e)}
          onDragLeave={(e) => dragLeaveHandler(e)}
          onDragOver={(e) => dragStartHandler(e)}
        >
          {showLoaderConversion && loadingConversion && (
            <span className="loading-text" style={{ marginBottom: '30px' }}>
              {t('Конвертируем изображение...')}
            </span>
          )}
          {showLoader && loading && (
            <span className="loading-text" style={{ marginBottom: '30px' }}>
              {t('Сжимаем изображение...')}
            </span>
          )}
          {isLoading && <AppLoader loaderType={LoaderType.table} />}
          {!disabled && (
            <div className="drag-block-wrap">
              <div className="link-wrap">
                <i className="an-ico an-ico-clip" />
                <div className="link-block">
                  <span>
                    {t('Перетащите')} <br /> {t('или')}{' '}
                    <AppInputFile
                      onChangeFile={onSetFile}
                      setLoading={handleLoading}
                      setLoadingConversion={handleLoadingConversion}
                    />
                  </span>
                  <br />
                  <div className="mt-10" />
                  <small>
                    {t(
                      'Максимальный размер файла 300 Мбайт. Рекомендуемый размер 800 на 600',
                    )}
                  </small>
                </div>
              </div>
              {setFileFromCamera && !disabled && (
                <div
                  className="photo-upload-btn-wrap"
                  onClick={() => setFileFromCamera(true)}
                >
                  <div className="btn-upload-photo">
                    <i className="an-ico an-ico-camera" />
                    <span>{t('Сфотографировать станцией')}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}
